"use client";

import {
  useCallback,
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { useQuery } from "@rocicorp/zero/react";
import { queries } from "@/lib/queries";
import { useUserList } from "@/lib/selectors";
import { useOpenDM } from "@/hooks/use-open-dm";

export interface TerminalResponseLinkContextValue {
  onMentionClick: (userId: string) => void;
  onChannelClick: (channelId: string) => void;
  users: { id: string; display_name: string }[];
  channels: { id: string; name: string }[];
}

const TerminalResponseLinkContext =
  createContext<TerminalResponseLinkContextValue | null>(null);

export function useTerminalResponseLink(): TerminalResponseLinkContextValue | null {
  return useContext(TerminalResponseLinkContext);
}

export function TerminalResponseLinkProvider({
  workspaceId,
  openChannelWithContext,
  children,
}: {
  workspaceId: string;
  openChannelWithContext: (
    channelId: string,
    opts?: { workspaceId: string | null },
  ) => void;
  children: ReactNode;
}) {
  const [userList] = useUserList();
  const [channelsRaw] = useQuery(
    workspaceId ? queries.channels.byWorkspaceAndUser(workspaceId) : null,
  );
  const channels = useMemo(() => channelsRaw ?? [], [channelsRaw]);
  const onMentionClick = useOpenDM((id) =>
    openChannelWithContext(id, { workspaceId }),
  );
  const onChannelClick = useCallback(
    (channelId: string) => {
      openChannelWithContext(channelId, { workspaceId });
    },
    [openChannelWithContext, workspaceId],
  );

  const value = useMemo<TerminalResponseLinkContextValue>(
    () => ({
      onMentionClick,
      onChannelClick,
      users: userList.map((u: { id: string; display_name: string }) => ({
        id: u.id,
        display_name: u.display_name,
      })),
      channels: channels.map((c: { id: string; name: string }) => ({
        id: c.id,
        name: c.name,
      })),
    }),
    [onMentionClick, onChannelClick, userList, channels],
  );

  return (
    <TerminalResponseLinkContext.Provider value={value}>
      {children}
    </TerminalResponseLinkContext.Provider>
  );
}
